import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../services/api'

export const usePartnerStore = defineStore('partner', () => {
  // State
  const partner = ref(null)
  const accessiblePartners = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const isPartnerLoaded = computed(() => partner.value !== null)
  const partnerId = computed(() => partner.value?.PartnerID)

  // Actions
  const fetchPartner = async () => {
    try {
      loading.value = true
      error.value = null
      
      // Check if there's a stored partner preference
      const storedPartnerId = localStorage.getItem('selectedPartnerId')
      
      // The interceptor in api.js will automatically add the Authorization header
      // Pass the stored partner ID as a query parameter if it exists
      const url = storedPartnerId 
        ? `/partners/me?preferredPartnerId=${storedPartnerId}`
        : '/partners/me'
      
      const response = await api.get(url)
      
      partner.value = response.data.data
      
      // Update localStorage with the actual partner ID returned (in case preferred wasn't valid)
      if (partner.value?.PartnerID) {
        localStorage.setItem('selectedPartnerId', partner.value.PartnerID.toString())
      }
    } catch (err) {
      console.error('Error fetching partner:', err)
      error.value = err.response?.data?.error || 'Failed to fetch partner data'
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchAccessiblePartners = async () => {
    try {
      // The interceptor in api.js will automatically add the Authorization header
      const response = await api.get('/partners/accessible')
      
      accessiblePartners.value = response.data.data || []
    } catch (err) {
      console.error('Error fetching accessible partners:', err)
      accessiblePartners.value = []
    }
  }

  const switchPartner = async (partnerId) => {
    try {
      loading.value = true
      error.value = null
      
      // Store selected partner ID in localStorage before switching
      localStorage.setItem('selectedPartnerId', partnerId.toString())
      
      // The interceptor in api.js will automatically add the Authorization header
      const response = await api.get(`/partners/${partnerId}`)
      
      partner.value = response.data.data
      
      // Refresh the page to reload all data with new partner context
      window.location.reload()
    } catch (err) {
      console.error('Error switching partner:', err)
      error.value = err.response?.data?.error || 'Failed to switch partner'
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearPartner = () => {
    partner.value = null
    accessiblePartners.value = []
    error.value = null
  }

  return {
    // State
    partner,
    accessiblePartners,
    loading,
    error,
    
    // Getters
    isPartnerLoaded,
    partnerId,
    
    // Actions
    fetchPartner,
    fetchAccessiblePartners,
    switchPartner,
    clearPartner
  }
}) 